/* MY BABY: quick-add sheet behaviour.

   What the theme does not do on its own.

   1. The sheet header carries a product thumbnail. Rather than tracking the
      selected variant a second time, it mirrors whichever slide the theme's
      <product-images> has marked .media--active, so it follows the variant
      picker by construction.

   2. Add to cart slides the sheet down on the tap (theme:cart:add fires before
      the request goes out) and shows a pending toast, but the sheet stays in the
      DOM, hidden, until the cart answers. That matters: the theme reports a
      failed add by dispatching theme:product:add-error on the submit BUTTON,
      and a detached button cannot bubble to document. Remove the sheet early
      and a late failure is never heard: the pending toast spins forever and the
      error message is lost. On success the sheet is removed; on failure it
      slides back up with the theme's inline error on it.

   3. The motion is driven by our own .mb-closing class, never the theme's
      [closing] attribute. [closing] also animates the dialog itself, and the
      theme removes the dialog on that animationend, 500ms after the tap and
      before any cart answer. Our class only animates the panel, which the
      theme's dialog-level listener ignores.

   4. Quick adds never open the cart drawer: the shopper is mid-browse, so the
      counter bumps and the toast reports instead. The product page, and a quick
      add launched from inside the cart drawer's own upsells, keep the drawer.
      Enforced in cart-drawer.js (section 10); this file stamps where each sheet
      was launched from (data-mb-launch).

   5. Every add is tracked on its own button, so overlapping adds cannot settle
      each other, and a slow answer is never reported as a failure while the
      request is still in flight (2026-09-23, after the demo's ghost drawer).

   6. Visible or closed, never between: parked sheets are inert, a sheet that
      opened but never reached the screen is settled, and a second sheet of a
      product whose first sheet is still parked opens normally.

   Loaded from head.liquid, after theme.js. */

(function () {
  'use strict';

  var MODAL = '[data-quick-add-modal]';
  var IMAGES = 'product-images';
  var ACTIVE_SLIDE = '.product__slide.media--active';
  var THUMB = '[data-quick-add-thumb]';
  var CONTENT = '.product-quick-add__content';
  /* the theme's own close attribute; read here, never set */
  var CLOSING = 'closing';
  /* ours: plays the slide, see mb-quick-add.css */
  var CLOSING_CLASS = 'mb-closing';
  /* ours: slid away but kept in the DOM until the cart answers */
  var GONE_CLASS = 'mb-gone';
  /* the slide is 500ms (300 on desktop); the guard only matters if animationend never lands */
  var ANIMATION_FALLBACK_MS = 900;
  /* if neither an added nor an error event arrives: first look after 8s, then
     every 3s while the request is still in flight, and ask the cart after 45s */
  var OUTCOME_FALLBACK_MS = 8000;
  var OUTCOME_RECHECK_MS = 3000;
  var OUTCOME_LIMIT_MS = 45000;
  /* where the sheet was launched from: 'drawer' (the cart drawer's upsells) or
     'page'. Stamped when the theme creates the sheet; cart-drawer.js reads it to
     decide whether the add may bring the drawer back. */
  var LAUNCH_ATTR = 'data-mb-launch';
  /* the sheet watchdog looks this long after an open */
  var SHEET_WATCH_MS = 900;
  var TOAST_MS = 2600;

  var toastTimer = null;

  /* ---------- header thumbnail ---------- */

  function activeImage(modal) {
    var images = modal.querySelector(IMAGES);
    if (!images) return null;
    var slide = images.querySelector(ACTIVE_SLIDE) || images.querySelector('.product__slide');
    return slide ? slide.querySelector('img') : null;
  }

  function syncThumb(modal) {
    var holder = modal.querySelector(THUMB);
    if (!holder) return;
    var source = activeImage(modal);
    if (!source) return;

    var target = holder.querySelector('img');
    if (!target) {
      target = document.createElement('img');
      target.alt = '';
      target.setAttribute('sizes', '88px');
      holder.appendChild(target);
    }

    /* Copy the srcset too, so the thumbnail keeps picking a sensibly sized file
       instead of pulling the full-width variant image down a phone connection. */
    var srcset = source.getAttribute('srcset') || source.getAttribute('data-srcset') || '';
    var src = source.getAttribute('src') || source.currentSrc || '';
    if (srcset && target.getAttribute('srcset') !== srcset) target.setAttribute('srcset', srcset);
    if (src && target.getAttribute('src') !== src) target.setAttribute('src', src);
  }

  function watchThumb(modal) {
    var images = modal.querySelector(IMAGES);
    if (!images || images.dataset.mbThumbWatched) return;
    images.dataset.mbThumbWatched = 'true';

    syncThumb(modal);
    /* data-active-media changes on the host and the slides swap .media--active
       between themselves: watch both rather than guessing which lands first. */
    new MutationObserver(function () {
      syncThumb(modal);
    }).observe(images, {
      attributes: true,
      attributeFilter: ['data-active-media', 'class'],
      subtree: true,
    });
  }

  /* ---------- cart count + toast ---------- */

  function cartCount() {
    var node = document.querySelector('[data-cart-count]');
    if (!node) return null;
    var n = parseInt(node.getAttribute('data-cart-count'), 10);
    return isFinite(n) ? n : null;
  }

  /* The sheet leaves on the tap, so between the tap and the cart answering there
     would be nothing on screen at all. The toast covers that gap: it opens in a
     pending state the moment the request goes out and settles into the result. */
  function showToast(state) {
    var strings = window.mbQuickAddStrings || {};
    var toast = document.querySelector('.mb-added-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'mb-added-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      document.body.appendChild(toast);
    }

    toast.classList.toggle('is-pending', state === 'pending');
    toast.classList.toggle('is-failed', state === 'failed');
    toast.textContent = '';

    if (state === 'pending') {
      var spinner = document.createElement('i');
      spinner.className = 'mb-added-toast__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      toast.appendChild(spinner);
      toast.appendChild(document.createTextNode(strings.adding || 'Adding to cart'));
    } else if (state === 'failed') {
      toast.appendChild(document.createTextNode(strings.failed || 'Could not add to cart'));
    } else {
      var count = cartCount();
      toast.appendChild(document.createTextNode((strings.added || 'Added') + ' '));
      if (count !== null) {
        var badge = document.createElement('b');
        badge.textContent = count;
        toast.appendChild(badge);
      }
    }

    /* force a frame so the transition runs on a freshly created node */
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    clearTimeout(toastTimer);
    /* pending has no timer of its own: the outcome (or its 8s fallback) settles it */
    if (state !== 'pending') {
      toastTimer = setTimeout(function () {
        toast.classList.remove('is-visible');
      }, TOAST_MS);
    }
  }

  function hideToast() {
    var toast = document.querySelector('.mb-added-toast');
    clearTimeout(toastTimer);
    if (toast) toast.classList.remove('is-visible');
  }

  /* ---------- the card that launched the sheet ---------- */

  /* A parked sheet whose product was opened again keeps its id under
     data-mb-product-id (see unmatchParked), so the theme cannot pick it up. */
  function productIdOf(modal) {
    return modal.getAttribute('data-product-id') || modal.getAttribute('data-mb-product-id');
  }

  function holdersFor(modal) {
    var id = productIdOf(modal);
    if (!id) return [];
    return Array.prototype.slice.call(document.querySelectorAll('[data-quick-add-holder="' + id + '"]'));
  }

  /* Opening the sheet puts the launching card into .is-disabled, which is
     pointer-events: none. The theme cleared that in a close we now bypass, so
     without this the card's button goes dead after its first use: still red,
     still labelled, completely unclickable. Every copy of the product on the
     page is released, so it cannot matter which one launched the sheet. */
  function releaseHolders(modal) {
    holdersFor(modal).forEach(function (holder) {
      holder.classList.remove('is-disabled');
      var button = holder.querySelector('[data-quick-add-modal-handle]');
      if (button) {
        button.disabled = false;
        button.classList.remove('is-loading', 'loading');
      }
    });
  }

  /* ---------- focus + scroll bookkeeping the theme did in the close we bypass ---------- */

  function a11y() {
    return window.theme && window.theme.a11y ? window.theme.a11y : null;
  }

  function releaseFocus() {
    var api = a11y();
    if (!api) return;
    if (typeof api.removeTrapFocus === 'function') api.removeTrapFocus();
    /* window.a11y.lastElement is the card button; the theme set it on open */
    if (typeof api.autoFocusLastElement === 'function') api.autoFocusLastElement();
  }

  function trapFocus(modal) {
    var api = a11y();
    if (api && typeof api.trapFocus === 'function') api.trapFocus(modal);
  }

  function anotherOverlayOpen(except) {
    if (document.querySelector('cart-drawer.is-open')) return true;
    var open = document.querySelectorAll(MODAL + '[open]');
    for (var i = 0; i < open.length; i++) {
      if (open[i] !== except && !open[i].classList.contains(GONE_CLASS)) return true;
    }
    return false;
  }

  function unlockScroll(except) {
    if (anotherOverlayOpen(except)) return;
    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
  }

  /* ---------- closing ---------- */

  var pending = [];

  function entryFor(modal) {
    for (var i = 0; i < pending.length; i++) {
      if (pending[i].modal === modal) return pending[i];
    }
    return null;
  }

  function drop(entry) {
    var index = pending.indexOf(entry);
    if (index > -1) pending.splice(index, 1);
    clearTimeout(entry.animationTimer);
    clearTimeout(entry.outcomeTimer);
    if (entry.content) entry.content.removeEventListener('animationend', entry.onEnd);
  }

  /* Slid away, cart not yet answered: off screen, out of the tab order, page
     scroll handed back so the shopper carries on, but still attached so the
     theme's error event can still reach us. */
  function park(entry) {
    if (entry.parked) return;
    entry.parked = true;
    entry.modal.classList.add(GONE_CLASS);
    /* hidden is not enough: nothing in a parked sheet may take a tap */
    entry.modal.setAttribute('inert', '');
    releaseFocus();
    unlockScroll(entry.modal);
  }

  function remove(entry) {
    var modal = entry.modal;
    drop(entry);
    releaseHolders(modal);
    if (!entry.parked) releaseFocus();
    modal.classList.remove(CLOSING_CLASS, GONE_CLASS);
    try {
      if (typeof modal.close === 'function' && modal.open) modal.close();
    } catch (error) {
      /* a dialog opened with show() rather than showModal() can refuse to close
         in some engines; the attribute path below still applies */
    }
    modal.removeAttribute('open');
    modal.setAttribute('inert', '');
    if (modal.parentNode) modal.parentNode.removeChild(modal);
    unlockScroll(null);
  }

  /* Back up into view so the shopper reads the error on the sheet that raised
     it. Removing our classes lets the theme's [open] rules replay the slide-up. */
  function restore(entry) {
    var modal = entry.modal;
    drop(entry);
    modal.classList.remove(GONE_CLASS, CLOSING_CLASS);
    modal.removeAttribute('inert');
    if (entry.parked) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
      trapFocus(modal);
    }
  }

  /* waitForCart: the add-to-cart slide plays straight away but only lets go of
     the sheet once the cart answers, so a failure can put it back. A plain close
     has nothing to wait for, and runs the identical motion.

     Every add is tracked by its own entry and its own button. The theme reports
     each add on that button (theme:product:add on success, theme:product:add-error
     on failure, both bubbling), so one answer can never settle another sheet's
     add. The old version kept one global flag and confirmed every parked sheet on
     the first theme:product:added, which fires after ANY cart rebuild. */
  function slideDown(modal, waitForCart, button) {
    if (!modal || entryFor(modal)) return null;

    var entry = {
      modal: modal,
      button: button || null,
      content: modal.querySelector(CONTENT),
      animationDone: false,
      confirmed: !waitForCart,
      parked: false,
      countAtTap: cartCount(),
      startedAt: Date.now(),
      /* A browsing add reports in the toast. A sheet launched from the cart
         drawer's own upsells goes back to the drawer, which reports for itself. */
      toast: !!waitForCart && modal.getAttribute(LAUNCH_ATTR) !== 'drawer',
    };

    entry.finishIfReady = function () {
      if (!entry.animationDone) return;
      if (entry.confirmed) remove(entry);
      else park(entry);
    };

    entry.onEnd = function (event) {
      if (event.target !== entry.content) return;
      entry.animationDone = true;
      entry.finishIfReady();
    };

    pending.push(entry);

    modal.classList.add(CLOSING_CLASS);
    if (entry.content) entry.content.addEventListener('animationend', entry.onEnd);

    entry.animationTimer = setTimeout(function () {
      entry.animationDone = true;
      entry.finishIfReady();
    }, ANIMATION_FALLBACK_MS);

    if (waitForCart) {
      entry.outcomeTimer = setTimeout(function () {
        checkOutcome(entry);
      }, OUTCOME_FALLBACK_MS);
    }
    return entry;
  }

  function entryForButton(button) {
    if (!button) return null;
    for (var i = 0; i < pending.length; i++) {
      var entry = pending[i];
      if (entry.button === button || (entry.modal.contains && entry.modal.contains(button))) return entry;
    }
    return null;
  }

  function inFlight(entry) {
    var b = entry.button;
    return !!b && b.classList.contains('is-loading');
  }

  /* ---------- toast bookkeeping ---------- */

  /* The theme reports the add on the button BEFORE it rebuilds the cart, and the
     header counter is refreshed by that rebuild. Wait for it (theme:product:added)
     so the toast shows the new count, with a timeout in case it never comes. A
     timeout, not a frame callback: a backgrounded or throttled tab never runs the
     frame. */
  var addedToastDue = false;
  var addedToastTimer = null;

  function queueAddedToast() {
    addedToastDue = true;
    clearTimeout(addedToastTimer);
    addedToastTimer = setTimeout(flushAddedToast, 1500);
  }

  function flushAddedToast() {
    if (!addedToastDue) return;
    addedToastDue = false;
    clearTimeout(addedToastTimer);
    showToast('added');
  }

  document.addEventListener('theme:product:added', function () {
    if (addedToastDue) setTimeout(flushAddedToast, 60);
  });

  /* ---------- outcomes ---------- */

  function confirmEntry(entry) {
    if (entry.confirmed) return;
    entry.confirmed = true;
    clearTimeout(entry.outcomeTimer);
    entry.finishIfReady();
    if (entry.toast) queueAddedToast();
  }

  function failEntry(entry) {
    restore(entry);
    if (entry.toast) showToast('failed');
  }

  /* No answer yet. While the request is still in flight (the theme keeps
     .is-loading on the button until it answers) the sheet stays parked and the
     toast keeps saying "adding": a slow phone connection or a suspended tab is
     not a failure. Declaring one early brought the sheet back as "failed" and
     then let the late success land on top of it. Only when nothing is in flight
     any more, or after OUTCOME_LIMIT_MS, ask the cart itself. */
  function checkOutcome(entry) {
    if (pending.indexOf(entry) < 0 || entry.confirmed) return;
    if (inFlight(entry) && Date.now() - entry.startedAt < OUTCOME_LIMIT_MS) {
      entry.outcomeTimer = setTimeout(function () {
        checkOutcome(entry);
      }, OUTCOME_RECHECK_MS);
      return;
    }
    var root = (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
    fetch(root + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        if (pending.indexOf(entry) < 0 || entry.confirmed) return;
        var added = entry.countAtTap === null || cart.item_count > entry.countAtTap;
        if (added) confirmEntry(entry);
        else failEntry(entry);
      })
      .catch(function () {
        if (pending.indexOf(entry) < 0 || entry.confirmed) return;
        failEntry(entry);
      });
  }

  document.addEventListener('theme:cart:add', function (event) {
    var button = event.detail && event.detail.button;
    var modal = button && button.closest ? button.closest(MODAL) : null;
    if (!modal || !modal.hasAttribute('open')) return;

    var entry = slideDown(modal, true, button);
    if (!entry) return;
    if (entry.toast) showToast('pending');

    /* The theme can decline an add without sending anything (an invalid form, a
       button it thinks is busy). It marks the button .is-loading synchronously
       when it does send, so one tick later no mark means no request: bring the
       sheet back at once instead of leaving it parked with a spinning toast. */
    setTimeout(function () {
      if (pending.indexOf(entry) < 0 || entry.confirmed) return;
      if (button.classList.contains('is-loading') || button.classList.contains('is-added')) return;
      restore(entry);
      if (entry.toast) hideToast();
    }, 0);
  });

  document.addEventListener('theme:product:add', function (event) {
    var button = (event.detail && event.detail.button) || event.target;
    var entry = entryForButton(button);
    if (entry) confirmEntry(entry);
  });

  document.addEventListener('theme:product:add-error', function (event) {
    var button = (event.detail && event.detail.button) || event.target;
    var entry = entryForButton(button);
    if (entry) failEntry(entry);
  });

  document.addEventListener('theme:cart:error', function (event) {
    var entry = entryForButton(event.target);
    if (entry && !entry.confirmed) failEntry(entry);
  });

  /* ---------- the theme's own close-on-added ---------- */

  /* The theme closes the sheet from theme:product:added through its [closing]
     handshake, which would fight the flow above. Leave it only the button reset.

     It binds this handler onto each instance in the constructor, so the
     prototype patch reaches only cards constructed after this script ran; the
     cards that came with the page keep their own bound copy and are patched
     one by one in scan(). The listener is registered at open time from the own
     property, so patching before the first open is enough. */
  function neutraliseCard(el) {
    if (!el || el.mbQuickAddNeutralised) return;
    el.mbQuickAddNeutralised = true;
    el.modalCloseOnProductAdded = function () {
      if (typeof el.resetQuickAddButtons === 'function') el.resetQuickAddButtons();
    };
  }

  function neutraliseCards() {
    Array.prototype.forEach.call(document.querySelectorAll('quick-add-product'), neutraliseCard);
  }

  /* ---------- the theme's sheet creation ---------- */

  /* The theme appends a fresh dialog and then takes the FIRST dialog in the
     document with the product's id as "the" sheet. While an earlier sheet of the
     same product is parked (hidden, waiting for its cart answer) that first match
     is the parked one: the content went into a hidden dialog, the page locked,
     and nothing appeared. So before the theme looks, a parked sheet of that
     product gives up the attribute the theme matches on, and a closed leftover
     of it is removed.

     Every sheet also renders the same form id (section.id is constant for the
     ?section_id= render), and inputs tied to it with form= bind to the first
     form in the document. With a parked sheet still attached that is the wrong
     one, so its ids are made unique first, the same way the theme's own
     HTMLUpdateUtility de-duplicates a node it is about to replace. */
  var parkSeq = 0;

  function dedupeIds(modal) {
    if (modal.hasAttribute('data-mb-ids-parked')) return;
    modal.setAttribute('data-mb-ids-parked', '');
    var suffix = '--mb' + (++parkSeq);
    var map = {};
    var withId = modal.querySelectorAll('[id]');
    Array.prototype.forEach.call(withId, function (node) {
      map[node.id] = node.id + suffix;
    });
    Array.prototype.forEach.call(withId, function (node) {
      node.id = map[node.id];
    });
    ['form', 'for', 'aria-controls', 'aria-labelledby', 'aria-describedby'].forEach(function (attr) {
      Array.prototype.forEach.call(modal.querySelectorAll('[' + attr + ']'), function (node) {
        var value = node.getAttribute(attr);
        var next = value.split(/\s+/).map(function (token) {
          return map[token] || token;
        }).join(' ');
        if (next !== value) node.setAttribute(attr, next);
      });
    });
  }

  function unmatchParked(productId) {
    pending.forEach(function (entry) {
      dedupeIds(entry.modal);
      if (productId && entry.modal.getAttribute('data-product-id') === String(productId)) {
        entry.modal.setAttribute('data-mb-product-id', productId);
        entry.modal.removeAttribute('data-product-id');
      }
    });
    if (!productId) return;
    Array.prototype.forEach.call(document.querySelectorAll(MODAL + '[data-product-id="' + productId + '"]:not([open])'), function (leftover) {
      if (!entryFor(leftover) && leftover.parentNode) leftover.parentNode.removeChild(leftover);
    });
  }

  /* 'drawer' when the card that launched the sheet sits in the cart drawer, and
     a sheet opened from inside another (the sibling swatches) inherits. */
  function launchOf(card) {
    if (card.closest('cart-drawer')) return 'drawer';
    var outer = card.closest(MODAL);
    return (outer && outer.getAttribute(LAUNCH_ATTR)) || 'page';
  }

  function patchTheme() {
    var Klass = window.customElements && window.customElements.get('quick-add-product');
    if (!Klass || Klass.prototype.mbQuickAdd) return;
    Klass.prototype.mbQuickAdd = true;
    Klass.prototype.modalCloseOnProductAdded = function () {
      if (typeof this.resetQuickAddButtons === 'function') this.resetQuickAddButtons();
    };

    /* not bound in the constructor, so the prototype reaches every card */
    var create = Klass.prototype.modalCreate;
    if (typeof create === 'function') {
      Klass.prototype.modalCreate = function () {
        var launch = launchOf(this);
        unmatchParked(this.productId);
        var result = create.apply(this, arguments);
        if (this.modal) this.modal.setAttribute(LAUNCH_ATTR, launch);
        return result;
      };
    }
    neutraliseCards();
  }

  if (window.customElements) {
    if (window.customElements.get('quick-add-product')) patchTheme();
    else window.customElements.whenDefined('quick-add-product').then(patchTheme);
  }

  /* ---------- description: read more in place ---------- */

  function wireDescription(modal) {
    var toggle = modal.querySelector('[data-quick-add-toggle]');
    var body = modal.querySelector('[data-quick-add-description]');
    var full = modal.querySelector('[data-quick-add-full]');
    if (!toggle || !body || toggle.dataset.mbWired) return;
    toggle.dataset.mbWired = 'true';

    var actions = toggle.parentNode;
    var content = modal.querySelector(CONTENT);

    /* Nothing to open if the copy already fits: hide the control rather than
       offering a toggle that does nothing. Measured again as images in the copy
       finish loading, since they arrive after the first measurement. */
    function refit() {
      if (!actions || body.classList.contains('is-expanded')) return;
      actions.hidden = body.scrollHeight <= body.clientHeight + 4;
    }
    refit();
    Array.prototype.forEach.call(body.querySelectorAll('img'), function (img) {
      if (!img.complete) img.addEventListener('load', refit, { once: true });
    });

    toggle.addEventListener('click', function () {
      var open = body.classList.toggle('is-expanded');
      /* On a phone the sheet takes a definite height while the description is
         open, which is what lets the description flex into the space left. */
      if (content) content.classList.toggle('is-desc-open', open);
      if (!open) body.scrollTop = 0;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.textContent = open
        ? toggle.getAttribute('data-label-less')
        : toggle.getAttribute('data-label-more');
      /* The full-page link is the end of the long read, so it only appears
         once the shopper has asked for the long read. */
      if (full) full.hidden = !open;
    });
  }

  /* ---------- the cart drawer stays shut on a quick add ----------
     Moved to assets/cart-drawer.js (section 10) on 2026-09-23: the drawer now
     opens from a cart rebuild only on the shopper's intent, and a sheet add is
     not one unless the sheet was launched from the drawer (data-mb-launch). */

  /* ---------- native scroll feel ----------
     vendor.js's scroll-lock watches touchmove on the document while the page is
     locked and cancels any move that would push a scroller past its edge, which
     is what makes a panel stop dead instead of bouncing like an app. Every
     scroller here carries overscroll-behavior: contain, so the browser already
     refuses to hand the scroll on to the page and the guard buys nothing. Keep
     touches inside an overflowing scroller from reaching the library, and the
     browser scrolls and rubber-bands it natively. Same treatment as the cart
     drawer. Passive: this only stops propagation. */
  function wireNativeScroll(modal) {
    if (modal.dataset.mbNativeScroll) return;
    modal.dataset.mbNativeScroll = 'true';
    modal.addEventListener(
      'touchmove',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var scroller = target.closest('[data-quick-add-description], [data-scroll-lock-scrollable]');
        if (!scroller || !modal.contains(scroller)) return;
        if (scroller.scrollHeight > scroller.clientHeight + 1) event.stopPropagation();
      },
      { passive: true }
    );
  }

  /* ---------- backstop for a close the theme starts itself ---------- */

  /* Sibling swapping and theme:modal:close still go through the theme's own
     two-phase close: set [closing], then wait for an animationend on the dialog.
     When that event does not arrive the sheet is left open with the page scroll
     still locked. Whoever sets [closing], this makes sure the close finishes. */
  function guardClose(modal) {
    if (modal.dataset.mbCloseGuarded) return;
    modal.dataset.mbCloseGuarded = 'true';

    var timer = null;
    new MutationObserver(function () {
      if (modal.hasAttribute(CLOSING)) {
        if (timer || entryFor(modal)) return;
        timer = setTimeout(function () {
          timer = null;
          if (modal.hasAttribute(CLOSING) && modal.parentNode) {
            remove({ modal: modal, content: modal.querySelector(CONTENT), parked: false });
          }
        }, ANIMATION_FALLBACK_MS);
      } else if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }).observe(modal, { attributes: true, attributeFilter: [CLOSING] });
  }

  /* The theme's close early-returns while its own isAnimating flag is set, and
     that flag is only cleared by an animationend on the dialog. Miss one and
     every later close on that grid item is a no-op. Taking the click here in
     the capture phase means a close request always starts, whatever the theme's
     internal state, and the theme's own listener never runs to race it. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var modal = null;
      var closer = target.closest('[data-quick-add-modal-close]');
      if (closer) modal = closer.closest(MODAL);
      else if (target.matches(MODAL)) modal = target;
      if (!modal || !modal.hasAttribute('open')) return;
      event.preventDefault();
      event.stopPropagation();
      slideDown(modal, false);
    },
    true
  );

  /* The sheet is opened with show(), not showModal(), so it never lands in the
     top layer and Escape does not reach it. Route the key to the same close,
     aimed at the sheet on top when more than one is open. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.code !== 'Escape') return;
    var open = document.querySelectorAll(MODAL + '[open]');
    var modal = null;
    for (var i = open.length - 1; i >= 0; i--) {
      var candidate = open[i];
      if (candidate.classList.contains(CLOSING_CLASS) || candidate.classList.contains(GONE_CLASS)) continue;
      if (candidate.hasAttribute(CLOSING)) continue;
      modal = candidate;
      break;
    }
    if (!modal) return;
    event.preventDefault();
    slideDown(modal, false);
  });

  /* ---------- visible or closed, never between ----------
     An open sheet whose content never reached the screen (the open animation
     stalled or never ran, a restored bfcache page, an accessibility tool's
     "stop animations" mode) is a full-screen layer nobody can see that still
     locks the page and takes taps. mb-quick-add.css now writes the open resting
     state out; this checks the result after every open, and when the page comes
     back, and settles it: finish the stalled motion, or close the sheet. */
  function showing(node) {
    if (!node) return false;
    var cs = getComputedStyle(node);
    if (cs.visibility !== 'visible' || parseFloat(cs.opacity) < 0.9) return false;
    var r = node.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.right > 1 && r.left < window.innerWidth - 1 && r.bottom > 1 && r.top < window.innerHeight - 1;
  }

  function finishAnimations(node) {
    if (!node || typeof node.getAnimations !== 'function') return;
    node.getAnimations().forEach(function (animation) {
      try { animation.finish(); } catch (error) {}
    });
  }

  function settleSheets() {
    Array.prototype.forEach.call(document.querySelectorAll(MODAL + '[open]'), function (modal) {
      if (entryFor(modal) || modal.hasAttribute(CLOSING)) return;
      if (modal.classList.contains(CLOSING_CLASS) || modal.classList.contains(GONE_CLASS)) return;
      var content = modal.querySelector(CONTENT);
      if (showing(content) && parseFloat(getComputedStyle(modal).opacity) >= 0.9) return;
      finishAnimations(modal);
      finishAnimations(content);
      if (showing(content) && parseFloat(getComputedStyle(modal).opacity) >= 0.9) return;
      if (window.console) console.warn('[quick-add] sheet open but not on screen; closing it');
      slideDown(modal, false);
    });
  }

  var sheetWatch = null;
  function scheduleSheetCheck() {
    clearTimeout(sheetWatch);
    sheetWatch = setTimeout(settleSheets, SHEET_WATCH_MS);
  }

  document.addEventListener('theme:quick-add:open', scheduleSheetCheck);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) settleSheets();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') scheduleSheetCheck();
  });

  /* ---------- wiring ---------- */

  function scan() {
    Array.prototype.forEach.call(document.querySelectorAll(MODAL), function (modal) {
      watchThumb(modal);
      guardClose(modal);
      wireDescription(modal);
      wireNativeScroll(modal);
    });
    neutraliseCards();
  }

  function start() {
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true });
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);

  document.addEventListener('theme:quick-add:open', scan);
  document.addEventListener('mb:filters:updated', neutraliseCards);
})();
